import { api, formatError, getCachedWorkspace, saveWorkspaceToConfig } from '../api-client'
import { analyticsCommandError, analyticsEvent, flushAndExit } from '../analytics'

interface TagRow {
  name: string
  description: string | null
  topic_count: number
  created_at: string
}

interface TagListPage {
  tags: TagRow[]
  total: number
  has_more: boolean
}

function emitJSON(data: unknown): void {
  console.log(JSON.stringify(data, null, 2))
}

// Workspace resolution mirrors context.ts: active workspace first (set by
// `workspace switch`/login), /me as fallback. Tags are workspace-level — no
// git repo required.
async function resolveWorkspaceSlug(): Promise<string> {
  const active = getCachedWorkspace().slug
  if (active) return active
  try {
    const me = (await api.get('/me', { timeoutMs: 8000, retries: 1 })) as {
      workspace_id?: string
      slug?: string
    }
    if (me?.slug) {
      saveWorkspaceToConfig(me.slug, me.workspace_id)
      return me.slug
    }
  } catch {
    /* fall through */
  }
  console.error('Error: could not resolve workspace. Run `driftless login` or `driftless workspace switch <slug>`.')
  process.exit(1)
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so TAGS_HELP still resolves from './tags'.
export { TAGS_HELP } from './help-text'
import { TAGS_HELP } from './help-text'

function printTagsHelp(out: NodeJS.WriteStream = process.stdout): void {
  out.write(TAGS_HELP)
}

export async function tagsCommand(args: string[]): Promise<void> {
  const isJSON = args.includes('--json')
  if (args.includes('--help') || args[0] === 'help') {
    printTagsHelp()
    process.exit(0)
  }
  const sub = args[0] && !args[0].startsWith('--') ? args[0] : undefined
  if (sub && sub !== 'add' && sub !== 'rename' && sub !== 'rm') {
    console.error(`Unknown subcommand '${sub}'. Usage: driftless tags [add|rename|rm] — see driftless tags --help`)
    process.exit(1)
  }

  const flagValue = (name: string): string | undefined => {
    const i = args.indexOf(`--${name}`)
    if (i === -1 || i === args.length - 1) return undefined
    const v = args[i + 1]
    return v.startsWith('--') ? undefined : v
  }

  const workspaceSlug = await resolveWorkspaceSlug()

  try {
    if (sub === 'add') {
      const name = args[1] && !args[1].startsWith('--') ? args[1] : undefined
      if (!name) {
        console.error('Usage: driftless tags add <name> [--description "..."]')
        process.exit(1)
      }
      const created = (await api.post(`/workspaces/${workspaceSlug}/tags`, {
        name,
        ...(flagValue('description') ? { description: flagValue('description') } : {}),
      })) as TagRow
      analyticsEvent('cli_tags', workspaceSlug, { action: 'add' })
      if (isJSON) {
        emitJSON(created)
      } else {
        console.log(`✓ tag '${created.name}' registered${created.description ? ` — ${created.description}` : ''}`)
        console.log(`  anchor topics to it:  driftless context update <slug> --tags ${created.name}`)
      }
      await flushAndExit(0)
    }

    if (sub === 'rename') {
      const oldName = args[1] && !args[1].startsWith('--') ? args[1] : undefined
      const newName = args[2] && !args[2].startsWith('--') ? args[2] : undefined
      if (!oldName || !newName) {
        console.error('Usage: driftless tags rename <old> <new>')
        process.exit(1)
      }
      const result = (await api.patch(
        `/workspaces/${workspaceSlug}/tags/${encodeURIComponent(oldName)}`,
        { name: newName },
      )) as { renamed: string; topics_updated: number }
      analyticsEvent('cli_tags', workspaceSlug, { action: 'rename' })
      if (isJSON) {
        emitJSON(result)
      } else {
        console.log(`✓ '${oldName}' → '${result.renamed}' — ${result.topics_updated} topic(s) updated`)
      }
      await flushAndExit(0)
    }

    if (sub === 'rm') {
      const name = args[1] && !args[1].startsWith('--') ? args[1] : undefined
      if (!name) {
        console.error('Usage: driftless tags rm <name>')
        process.exit(1)
      }
      const result = (await api.delete(
        `/workspaces/${workspaceSlug}/tags/${encodeURIComponent(name)}`,
      )) as { deleted: string; topics_updated: number }
      analyticsEvent('cli_tags', workspaceSlug, { action: 'rm' })
      if (isJSON) {
        emitJSON(result)
      } else {
        console.log(`✓ tag '${result.deleted}' deleted — detached from ${result.topics_updated} topic(s)`)
      }
      await flushAndExit(0)
    }

    // default — list (paginated, most-used first)
    const limit = flagValue('limit')
    const offset = flagValue('offset')
    const qs = new URLSearchParams()
    if (limit) qs.set('limit', limit)
    if (offset) qs.set('offset', offset)
    const page = (await api.get(
      `/workspaces/${workspaceSlug}/tags${qs.size > 0 ? `?${qs.toString()}` : ''}`,
    )) as TagListPage
    analyticsEvent('cli_tags', workspaceSlug, { action: 'list' })
    if (isJSON) {
      emitJSON(page)
      await flushAndExit(0)
    }
    if (page.tags.length === 0) {
      console.log('No tags yet.')
      console.log('\nPre-create one:   driftless tags add billing --description "Billing & payments rules"')
      console.log('Or just use one:  driftless context update <slug> --tags billing  (auto-registers)')
      await flushAndExit(0)
    }
    const width = Math.max(...page.tags.map((t) => t.name.length), 4)
    console.log(`▌ tags (${page.total})\n`)
    for (const t of page.tags) {
      const count = `${t.topic_count} topic${t.topic_count === 1 ? '' : 's'}`
      console.log(`  ${t.name.padEnd(width + 2)}${count.padStart(9)}${t.description ? `   ${t.description}` : ''}`)
    }
    if (page.has_more) {
      console.log(`\nShowing ${page.tags.length} of ${page.total} — page with --limit N --offset N`)
    }
    console.log(`\nFilter topics:  driftless context list --tag <name> · context search "<q>" --tag <name>`)
    await flushAndExit(0)
  } catch (e: any) {
    console.error(`Tags command failed: ${formatError(e)}`)
    await analyticsCommandError('tags', workspaceSlug, formatError(e))
    await flushAndExit(1)
  }
}
